import { Fragment } from "react";

import type { Strips } from "../domain/cyclePatterns";
import { STRIP_WORDS } from "../domain/cycleWords";
import { SYMPTOM_LABEL_COL } from "./SymptomMap";
import "./CycleStrips.css";

/** 0 empty · 1 light · 2 medium · 3 strong — the symptom map's scale (kit §27). */
const LEVEL_VAR = ["--bg", "--cyc-tint", "--cyc-line", "--cyc-ink"] as const;

/** Grid line before day `d`; the label column is line 1. */
const col = (day: number) => day + 1;

/**
 * One symptom by cycle day, a strip per cycle (kit §54, ADR-060): oldest at the top, the current
 * cycle at the bottom drawn up to today, the period days underlined, and the pattern's window a
 * column band behind every strip. One CSS grid, as the symptom map is, so the band, the cells,
 * the today rule and the axis share the same day columns. Everything drawn is `stripsFor`'s; the
 * component does no arithmetic of its own beyond placing it.
 */
export function CycleStrips({ strips }: { strips: Strips }) {
  const n = Math.max(1, strips.len);
  const w = strips.window;
  let r = w ? 2 : 1;
  const placed = strips.rows.map((row) => {
    const today = row.current && strips.todayDay !== null ? r++ : null;
    const cells = r++;
    const edge = r++;
    return { row, today, cells, edge };
  });
  const axis = r;

  const ov = strips.ovulationDay;
  const items: { day: number; text: string; align: "left" | "center" | "right" }[] = [{ day: 1, text: "1", align: "left" }];
  if (n >= 10) items.push({ day: 5, text: "5", align: "center" });
  if (ov !== null && ov > 5 && ov < n) items.push({ day: ov, text: `${ov} · ${STRIP_WORDS.ovulation}`, align: "center" });
  if (n > 1) items.push({ day: n, text: String(n), align: "right" });

  return (
    <div className="k-strips" role="img" aria-label={strips.ariaLabel}>
      <div className="k-strips__grid" style={{ gridTemplateColumns: `${SYMPTOM_LABEL_COL} repeat(${n}, minmax(0, 9px))` }} aria-hidden="true">
        {w ? (
          <>
            <span className="k-strips__winlabel" style={{ gridRow: 1, gridColumn: `${col(w.from)} / ${col(w.to) + 1}` }}>
              {w.from === w.to ? w.from : `${w.from}–${w.to}`}
            </span>
            <span className="k-strips__band" style={{ gridRow: `2 / ${axis}`, gridColumn: `${col(w.from)} / ${col(w.to) + 1}` }} />
          </>
        ) : null}

        {placed.map(({ row, today, cells, edge }) => (
          <Fragment key={row.start}>
            {today !== null && strips.todayDay !== null ? (
              <span className="k-strips__today" style={{ gridRow: today, gridColumn: col(strips.todayDay) }} />
            ) : null}
            <span className="k-strips__label" style={{ gridRow: cells, gridColumn: 1 }}>
              {row.label}
            </span>
            {row.cells.slice(0, n).map((lvl, d) => (
              <span
                key={d}
                className="k-strips__cell"
                style={{
                  gridRow: cells,
                  gridColumn: col(d + 1),
                  // an empty day inside the window lets the band show, so the window reads down the column
                  background: lvl === 0 && w && d + 1 >= w.from && d + 1 <= w.to ? "transparent" : `var(${LEVEL_VAR[lvl]})`,
                }}
              />
            ))}
            <span
              className="k-strips__period"
              style={{ gridRow: edge, gridColumn: `${col(1)} / ${col(Math.min(row.periodDays, row.cells.length, n)) + 1}` }}
            />
          </Fragment>
        ))}

        {items.map((it) => (
          <span
            key={`${it.day}-${it.text}`}
            className={`k-strips__axis k-strips__axis--${it.align}`}
            style={{ gridRow: axis, gridColumn: col(it.day) }}
          >
            {it.text}
          </span>
        ))}
      </div>
      <p className="k-strips__legend" aria-hidden="true">
        {STRIP_WORDS.legend}
      </p>
    </div>
  );
}
